"use client";

import React from "react";
import { Coffee, Loader, Send, CircleCheck, CircleAlert } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
export default function StartYourJourney() {
  return <section data-section-id="223479" id="lassen-sie-uns-sprechen" className="relative py-24 bg-background text-foreground overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-5xl h-full pointer-events-none opacity-20">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/20 rounded-full blur-[120px]" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-primary/10 rounded-full blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10 max-w-3xl">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold font-default text-foreground mb-4 flex items-center justify-center gap-3 tracking-tight">
            Sicherheitsanalyse anfordern
            <Coffee className="size-8 md:size-10 text-primary" />
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto font-default">
            Bereit für eine kompromisslose Absicherung Ihrer Infrastruktur? Kontaktieren Sie unsere Experten für eine erste strategische Beratung oder eine detaillierte Schwachstellenanalyse.
          </p>
        </div>

        <div className="bg-card/50 backdrop-blur-md rounded-xl shadow-2xl border border-border p-6 md:p-10">
          <FormProvider formKey="contact_form_contact" formId="contact_form_contact" sectionName="Start Your Journey" defaultValues={{
          full_name: "",
          email: "",
          phone: "",
          interest: "Netzwerksicherheit",
          message: ""
        }} validationRules={{
          full_name: {
            required: true,
            minLength: 2
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: true,
            pattern: "phone"
          },
          interest: {
            required: true
          }
        }} submitText="Anfrage Senden" successMessage="Vielen Dank. Ihre Anfrage wurde verschlüsselt übertragen. Ein Sicherheitsspezialist wird sich in Kürze bei Ihnen melden.">
            <JourneyForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function JourneyForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-500">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6 border border-primary/30">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-bold font-default text-foreground mb-2">Verbindung hergestellt</h3>
        <p className="text-muted-foreground mb-8 max-w-md font-default">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="min-w-[200px] border-primary/50 text-primary hover:bg-primary/10 rounded-sm">
          Neue Anfrage starten
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      {}
      <FormField control={control} name="interest" rules={{
      required: "Bitte wählen Sie einen Fokusbereich."
    }} render={({
      field
    }) => <FormItem className="flex flex-col items-center space-y-4 pb-6 border-b border-border">
            <FormLabel className="text-sm font-medium text-muted-foreground uppercase tracking-widest font-mono">Fokusbereich:</FormLabel>
            <FormControl>
              <ToggleGroup type="single" value={field.value} onValueChange={value => {
          if (value) field.onChange(value);
        }} className="flex flex-wrap justify-center gap-2" variant="outline" size="lg">
                <ToggleGroupItem value="Netzwerksicherheit" aria-label="Netzwerksicherheit" className="px-6 rounded-sm border-border data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:border-primary font-mono text-xs">
                  Netzwerk
                </ToggleGroupItem>
                <ToggleGroupItem value="Infrastruktur" aria-label="Infrastruktur" className="px-6 rounded-sm border-border data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:border-primary font-mono text-xs">
                  Infrastruktur
                </ToggleGroupItem>
                <ToggleGroupItem value="Cybersecurity" aria-label="Cybersecurity" className="px-6 rounded-sm border-border data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:border-primary font-mono text-xs">
                  Security
                </ToggleGroupItem>
                <ToggleGroupItem value="Beratung" aria-label="Beratung" className="px-6 rounded-sm border-border data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:border-primary font-mono text-xs">
                  Beratung
                </ToggleGroupItem>
              </ToggleGroup>
            </FormControl>
            <FormMessage className="font-mono text-xs text-destructive" />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="full_name" rules={{
        required: "Name ist erforderlich"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-wider">Vollständiger Name</FormLabel>
              <FormControl>
                <input {...field} type="text" placeholder="Max Mustermann" className="flex h-11 w-full rounded-sm border border-input bg-background/50 px-3 py-2 text-base md:text-sm ring-offset-background placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 transition-all font-default" />
              </FormControl>
              <FormMessage className="font-mono text-xs text-destructive" />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Telefonnummer ist erforderlich"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-wider">Telefonnummer</FormLabel>
              <FormControl>
                <input {...field} type="tel" placeholder="+49 123 456789" className="flex h-11 w-full rounded-sm border border-input bg-background/50 px-3 py-2 text-base md:text-sm ring-offset-background placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 transition-all font-default" />
              </FormControl>
              <FormMessage className="font-mono text-xs text-destructive" />
            </FormItem>} />
      </div>

      <FormField control={control} name="email" rules={{
      required: "E-Mail-Adresse ist erforderlich",
      pattern: {
        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
        message: "Ungültige E-Mail-Adresse"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-wider">E-Mail-Adresse</FormLabel>
            <FormControl>
              <input {...field} type="email" placeholder="m.mustermann@firma.de" className="flex h-11 w-full rounded-sm border border-input bg-background/50 px-3 py-2 text-base md:text-sm ring-offset-background placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 transition-all font-default" />
            </FormControl>
            <FormMessage className="font-mono text-xs text-destructive" />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-wider">Projektbeschreibung (Optional)</FormLabel>
            <FormControl>
              <Textarea {...field} placeholder="Beschreiben Sie kurz Ihre Anforderungen oder Ihre aktuelle Infrastruktur..." className="min-h-[120px] resize-y rounded-sm border-input bg-background/50 focus-visible:ring-1 focus-visible:ring-primary focus-visible:border-primary font-default" />
            </FormControl>
            <FormMessage className="font-mono text-xs text-destructive" />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-sm flex items-start gap-3 text-destructive">
          <CircleAlert className="size-5 shrink-0 mt-0.5" />
          <p className="text-xs font-mono uppercase">{submitError}</p>
        </div>}

      <Button type="submit" size="lg" disabled={isSubmitting} className="w-full text-base h-12 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_15px_rgba(0,212,255,0.3)] transition-all font-default font-bold uppercase tracking-widest">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Verschlüssele Daten...
          </> : <>
            Anfrage Senden
            <Send className="ml-2 size-5" />
          </>}
      </Button>
    </Form>;
}